import { HEAT, type HeatKey } from '../lib/course-heat';

// Clean inline line-icon per category — a placeholder for the eventual photoreal category
// image inside the cover's image frame. Kept as plain stroke paths (no external icon
// dependency) so the cover never needs an asset fetch.
const CATEGORY_ICONS: Record<string, JSX.Element> = {
  medical: (
    <g>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v9M7.5 12h9" />
    </g>
  ),
  military: <path d="M12 2.8l2.7 6.4 6.9.6-5.2 4.5 1.6 6.8-6-3.7-6 3.7 1.6-6.8-5.2-4.5 6.9-.6z" strokeLinejoin="round" />,
  law_enforcement: (
    <g strokeLinejoin="round">
      <path d="M12 2.6l7 3v5.8c0 4.9-3.1 8.1-7 9.3-3.9-1.2-7-4.4-7-9.3V5.6z" />
      <path d="M12 7.6l1.5 3 3.3.3-2.5 2.3.7 3.3L12 14.8l-3 1.7.7-3.3-2.5-2.3 3.3-.3z" fill="currentColor" stroke="none" />
    </g>
  ),
  hospitality: (
    <g>
      <path d="M12 3.6a5.2 5.2 0 0 0-5.2 5.2v1.8c0 2.7-1.5 3.8-1.5 5.2h13.4c0-1.4-1.5-2.5-1.5-5.2V8.8A5.2 5.2 0 0 0 12 3.6z" />
      <path d="M9.2 18.8a2.8 2.8 0 0 0 5.6 0" />
    </g>
  ),
  aviation: <path d="M3 13.2l18-7.4-7.3 18-2.2-8.1z" strokeLinejoin="round" />,
};

const DEFAULT_ICON = (
  <g>
    <rect x="4.5" y="4.5" width="15" height="15" rx="1.5" />
    <path d="M8 9h8M8 12.5h8M8 16h5" />
  </g>
);

function industryLabel(category: string): string {
  return category.replace(/_/g, ' ').toUpperCase();
}

export interface CourseCoverProps {
  category: string;
  targetLanguageName: string;
  courseTitle: string;
  heat: HeatKey;
  className?: string;
}

// A proper book-cover layout matching the Glossika audio-course covers: full-bleed heat
// gradient, top-left wordmark + industry, a centered image-frame emblem with an amber glow,
// and a centered title block (target language / edition / AUDIO COURSE pill) below it.
export default function CourseCover({ category, targetLanguageName, courseTitle, heat, className }: CourseCoverProps) {
  const palette = HEAT[heat];
  const icon = CATEGORY_ICONS[category] ?? DEFAULT_ICON;

  return (
    <div
      className={`relative overflow-hidden rounded-xl shrink-0 ${className ?? ''}`}
      style={{ aspectRatio: '5 / 7', background: palette.bg }}
    >
      {/* soft amber glow halo behind the image frame */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 36%, ${palette.accent}33 0%, transparent 62%)` }}
      />

      <div className="relative h-full flex flex-col px-[7%] py-[6%]">
        {/* top-left wordmark + industry */}
        <div className="text-left">
          <div
            className="font-heading uppercase font-semibold"
            style={{ letterSpacing: '.18em', color: palette.accent, fontSize: '1rem' }}
          >
            GLOSSIKA
          </div>
          <div
            className="font-body uppercase mt-1"
            style={{ letterSpacing: '.1em', color: '#f3efe6', fontSize: '.8rem' }}
          >
            {industryLabel(category)}
          </div>
        </div>

        {/* centered image frame (emblem placeholder) */}
        <div className="flex-1 flex items-center justify-center min-h-0">
          <div
            className="rounded-xl flex items-center justify-center"
            style={{
              width: '56%',
              aspectRatio: '3 / 4',
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${palette.accent}55`,
              boxShadow: `0 0 36px 6px ${palette.accent}4d`,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.3}
              strokeLinecap="round"
              style={{ width: '42%', height: '42%', color: palette.accent }}
            >
              {icon}
            </svg>
          </div>
        </div>

        {/* title block */}
        <div className="text-center">
          <div className="font-heading" style={{ color: '#f3efe6', fontSize: '1.8rem', lineHeight: 1.15 }}>
            Glossika {targetLanguageName}
          </div>
          <div className="font-heading mt-1" style={{ color: palette.accent, fontSize: '1.1rem' }}>
            {courseTitle}
          </div>
          <span
            className="inline-block mt-3 px-3 py-1 rounded-full font-body font-bold uppercase"
            style={{
              background: palette.badgeBg,
              color: palette.badgeText,
              fontSize: '.65rem',
              letterSpacing: '.08em',
            }}
          >
            Audio Course
          </span>
        </div>
      </div>
    </div>
  );
}
